import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { 
  Package, 
  Mail, 
  Handshake, 
  TrendingUp,
  Clock,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-zinc-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Products',
      value: stats?.total_products || 0,
      icon: Package,
      color: 'bg-blue-500',
      trend: null
    },
    {
      title: 'Contact Inquiries',
      value: stats?.total_contacts || 0,
      icon: Mail,
      color: 'bg-green-500',
      trend: `${stats?.recent_contacts_7_days || 0} this week`
    },
    {
      title: 'Dealer Applications',
      value: stats?.total_dealers || 0,
      icon: Handshake,
      color: 'bg-purple-500',
      trend: `${stats?.recent_dealers_7_days || 0} this week`
    },
    {
      title: 'Pending Items',
      value: (stats?.pending_contacts || 0) + (stats?.pending_dealers || 0),
      icon: Clock,
      color: 'bg-orange-500',
      trend: 'Requires attention'
    }
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-black mb-2">Dashboard</h1>
        <p className="text-zinc-600">Welcome back! Here's your website overview.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <Card key={index} className="border-2 border-zinc-200 hover:border-yellow-500 hover:shadow-lg transition-all">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
                {stat.trend && (
                  <span className="text-xs text-zinc-500">{stat.trend}</span>
                )}
              </div>
              <h3 className="text-2xl font-bold text-black mb-1">{stat.value}</h3>
              <p className="text-sm text-zinc-600">{stat.title}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pending Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Contacts */}
        <Card className="border-2 border-zinc-200">
          <CardHeader className="border-b border-zinc-200">
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-green-600" />
              Pending Contact Inquiries
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-black">{stats?.pending_contacts || 0}</p>
                <p className="text-sm text-zinc-600 mt-1">Awaiting response</p>
              </div>
              <a 
                href="/admin/contacts" 
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors"
              >
                View All
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Pending Dealers */}
        <Card className="border-2 border-zinc-200">
          <CardHeader className="border-b border-zinc-200">
            <CardTitle className="flex items-center gap-2">
              <Handshake className="w-5 h-5 text-purple-600" />
              Pending Dealer Applications
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-black">{stats?.pending_dealers || 0}</p>
                <p className="text-sm text-zinc-600 mt-1">Awaiting approval</p>
              </div>
              <a 
                href="/admin/dealers" 
                className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors"
              >
                Review
              </a>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Summary */}
      <Card className="border-2 border-zinc-200 mt-6">
        <CardHeader className="border-b border-zinc-200">
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-yellow-600" />
            Recent Activity (Last 7 Days)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-black">{stats?.recent_contacts_7_days || 0}</p>
                <p className="text-sm text-zinc-600">New Contacts</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Handshake className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-black">{stats?.recent_dealers_7_days || 0}</p>
                <p className="text-sm text-zinc-600">New Dealer Enquiries</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-black">{stats?.total_products || 0}</p>
                <p className="text-sm text-zinc-600">Active Products</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="mt-6 bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6">
        <h3 className="font-bold text-black mb-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-yellow-600" />
          Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <a 
            href="/admin/products" 
            className="p-4 bg-white border-2 border-zinc-200 rounded-lg hover:border-yellow-500 hover:shadow-md transition-all text-center"
          >
            <Package className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
            <p className="font-semibold text-black">Add Product</p>
          </a>
          <a 
            href="/admin/contacts" 
            className="p-4 bg-white border-2 border-zinc-200 rounded-lg hover:border-yellow-500 hover:shadow-md transition-all text-center"
          >
            <Mail className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
            <p className="font-semibold text-black">View Inquiries</p>
          </a>
          <a 
            href="/admin/catalog" 
            className="p-4 bg-white border-2 border-zinc-200 rounded-lg hover:border-yellow-500 hover:shadow-md transition-all text-center"
          >
            <TrendingUp className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
            <p className="font-semibold text-black">Update Catalog</p>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
